// Route Optimizer API Service
// Wraps all backend endpoints for the Route Optimizer app.
import apiService from '../../../services/apiService';

const BASE = '/routeoptimizer';

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export async function getCompanies() {
  return apiService.get(`${BASE}/companies`);
}

export async function createCompany(nameOrObj) {
  const name = typeof nameOrObj === 'string' ? nameOrObj : nameOrObj.name;
  return apiService.post(`${BASE}/companies`, { name });
}

export async function deleteCompany(cid) {
  return apiService.delete(`${BASE}/companies/${cid}`);
}

// ---------------------------------------------------------------------------
// Branches
// ---------------------------------------------------------------------------

export async function getBranches(cid) {
  return apiService.get(`${BASE}/companies/${cid}/branches`);
}

export async function createBranch(cid, { name, location, region }) {
  return apiService.post(`${BASE}/companies/${cid}/branches`, { name, location, region });
}

export async function deleteBranch(bid) {
  return apiService.delete(`${BASE}/branches/${bid}`);
}

export async function getBranchSummary(bid) {
  return apiService.get(`${BASE}/branches/${bid}/summary`);
}

// ---------------------------------------------------------------------------
// Weeks
// ---------------------------------------------------------------------------

export async function getWeeks(bid) {
  const resp = await apiService.get(`${BASE}/branches/${bid}/weeks`);
  // Handle both old (array) and new (object with weeks + metadata) formats
  if (Array.isArray(resp)) return { weeks: resp };
  return resp;
}

export async function createWeek(bid, nameOrObj) {
  const name = typeof nameOrObj === 'string' ? nameOrObj : nameOrObj.name;
  return apiService.post(`${BASE}/branches/${bid}/weeks`, { name });
}

export async function deleteWeek(wid) {
  return apiService.delete(`${BASE}/weeks/${wid}`);
}

// ---------------------------------------------------------------------------
// Company Settings
// ---------------------------------------------------------------------------

export async function getCompanySettings(cid) {
  return apiService.get(`${BASE}/companies/${cid}/settings`);
}

export async function saveCompanySettings(cid, settings) {
  return apiService.post(`${BASE}/companies/${cid}/settings`, settings);
}

// ---------------------------------------------------------------------------
// Branch Settings
// ---------------------------------------------------------------------------

export async function getBranchSettings(bid) {
  return apiService.get(`${BASE}/branches/${bid}/settings`);
}

export async function saveBranchSettings(bid, settings) {
  return apiService.post(`${BASE}/branches/${bid}/settings`, settings);
}

export async function resetBranchSettings(bid) {
  return apiService.post(`${BASE}/branches/${bid}/reset-settings`, {});
}

// ---------------------------------------------------------------------------
// Week Details & Settings
// ---------------------------------------------------------------------------

export async function getWeekDetails(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/details`);
}

export async function updateWeekSettings(wid, settings) {
  return apiService.post(`${BASE}/weeks/${wid}/settings`, settings);
}

export async function resetWeekSettings(wid) {
  return apiService.post(`${BASE}/weeks/${wid}/reset-settings`, {});
}

// ---------------------------------------------------------------------------
// Upload: Addresses & Workers
// ---------------------------------------------------------------------------

export async function uploadAddresses(wid, addresses) {
  return apiService.post(`${BASE}/weeks/${wid}/upload/addresses`, { addresses });
}

export async function mergeAddresses(wid, addresses) {
  return apiService.post(`${BASE}/weeks/${wid}/upload/addresses/merge`, { addresses });
}

export async function uploadAddressesFile(wid, file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiService.post(`${BASE}/weeks/${wid}/upload/addresses`, formData, { timeout: 60000 });
}

export async function uploadWorkers(wid, workers) {
  return apiService.post(`${BASE}/weeks/${wid}/upload/workers`, { workers });
}

export async function uploadWorkersFile(wid, file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiService.post(`${BASE}/weeks/${wid}/upload/workers`, formData, { timeout: 60000 });
}

// ---------------------------------------------------------------------------
// Previous Data (reuse last dataset)
// ---------------------------------------------------------------------------

export async function getPreviousData(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/previous-data`);
}

// ---------------------------------------------------------------------------
// Load Demo Data
// ---------------------------------------------------------------------------

export async function loadDemo(wid, simOrObj) {
  const simulation = typeof simOrObj === 'number' ? simOrObj : simOrObj?.simulation ?? 0;
  return apiService.post(`${BASE}/weeks/${wid}/load-demo`, { simulation });
}

// ---------------------------------------------------------------------------
// Build Distance Matrix
// ---------------------------------------------------------------------------

export async function buildMatrix(wid) {
  return apiService.post(`${BASE}/weeks/${wid}/build-matrix`, {}, { timeout: 30000 });
}

export async function getBuildMatrixStatus(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/build-matrix/status`);
}

// ---------------------------------------------------------------------------
// Optimize
// ---------------------------------------------------------------------------

export async function optimize(wid, { mode = 'standard' } = {}) {
  return apiService.post(`${BASE}/weeks/${wid}/optimize`, { mode }, { timeout: 30000 });
}

export async function getOptimizeStatus(wid, jobPk) {
  const q = jobPk ? `?job_pk=${encodeURIComponent(jobPk)}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/optimize/status${q}`);
}

// ---------------------------------------------------------------------------
// Feasibility Report
// ---------------------------------------------------------------------------

export async function getFeasibility(wid) {
  try {
    return await apiService.get(`${BASE}/weeks/${wid}/feasibility`);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Geocode Addresses
// ---------------------------------------------------------------------------

export async function geocodeAddresses(wid, force = false) {
  return apiService.post(`${BASE}/weeks/${wid}/geocode-addresses`, { force }, { timeout: 30000 });
}

export async function getGeocodeStatus(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/geocode-addresses/status`);
}

// ---------------------------------------------------------------------------
// Results, Geocoding, Geometries (tier-aware)
// ---------------------------------------------------------------------------

export async function getResults(wid, tier) {
  const q = tier ? `?tier=${tier}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/results${q}`);
}

export async function getResultsSummary(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/results/summary`);
}

export async function getGeocode(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/geocode`);
}

export async function getGeometries(wid, tier) {
  const q = tier ? `?tier=${tier}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/geometries${q}`);
}

// ---------------------------------------------------------------------------
// Route Itinerary
// ---------------------------------------------------------------------------

export async function getRouteItinerary(wid, { day, technician }) {
  const params = new URLSearchParams();
  if (day !== undefined) params.append('day', day);
  if (technician !== undefined) params.append('technician', technician);
  return apiService.get(`${BASE}/weeks/${wid}/route-itinerary?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export async function exportWeek(wid, { approval } = {}) {
  const params = approval ? `?approval=${encodeURIComponent(approval)}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/export${params}`);
}

// ---------------------------------------------------------------------------
// Quick Start
// ---------------------------------------------------------------------------

export async function quickStart(data) {
  return apiService.post(`${BASE}/quick-start`, data);
}

export async function quickStartBelgrade(data) {
  return apiService.post(`${BASE}/quick-start-belgrade`, data);
}

export async function quickStartMiami(data) {
  return apiService.post(`${BASE}/quick-start-miami`, data);
}

// ---------------------------------------------------------------------------
// Batch Optimize
// ---------------------------------------------------------------------------

export async function batchOptimize(weekIds) {
  return apiService.post(`${BASE}/batch-optimize`, { week_ids: weekIds });
}

export async function getBatchOptimizeStatus(batchId) {
  return apiService.get(`${BASE}/batch-optimize/${batchId}/status`);
}

// ---------------------------------------------------------------------------
// Optimizable Weeks (validation check)
// ---------------------------------------------------------------------------

export async function getOptimizableWeeks(weekIds) {
  return apiService.post(`${BASE}/optimizable-weeks`, { week_ids: weekIds });
}

// ---------------------------------------------------------------------------
// Approval & Run History
// ---------------------------------------------------------------------------

export async function approveRun(wid, { status, comment, tiers, total_miles }) {
  return apiService.post(`${BASE}/weeks/${wid}/approve`, { status, comment, tiers, total_miles });
}

export async function getRunHistory(wid) {
  try {
    return await apiService.get(`${BASE}/weeks/${wid}/run-history`);
  } catch {
    return []; // endpoint may not exist yet
  }
}

// ---------------------------------------------------------------------------
// Data Delta & Confirm Removals
// ---------------------------------------------------------------------------

export async function getDataDelta(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/data-delta`);
}

export async function confirmRemovals(wid, customerIds) {
  return apiService.post(`${BASE}/weeks/${wid}/confirm-removals`, { customer_ids: customerIds });
}

// ---------------------------------------------------------------------------
// Address & Worker Updates (inline editing)
// ---------------------------------------------------------------------------

export async function updateAddresses(wid, updates) {
  return apiService.post(`${BASE}/weeks/${wid}/addresses/update`, { updates });
}

export async function updateWorkers(wid, updates) {
  return apiService.post(`${BASE}/weeks/${wid}/workers/update`, { updates });
}

// ---------------------------------------------------------------------------
// Change Lock Override
// ---------------------------------------------------------------------------

export async function overrideLock(wid, index) {
  return apiService.post(`${BASE}/weeks/${wid}/addresses/${index}/override-lock`, {});
}

// ---------------------------------------------------------------------------
// Admin Settings
// ---------------------------------------------------------------------------

export async function getAdminSettings() {
  try {
    return await apiService.get(`${BASE}/admin/settings`);
  } catch {
    return {};
  }
}

export async function saveAdminSettings(data) {
  return apiService.post(`${BASE}/admin/settings`, data);
}

export async function getAdminRunHistory() {
  try {
    return await apiService.get(`${BASE}/admin/run-history`);
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Aliases (match component usage to service function names)
// ---------------------------------------------------------------------------

export const listCompanies = getCompanies;
export const listBranches = getBranches;
export const listWeeks = getWeeks;
export const saveSettings = updateWeekSettings;
export const exportSchedule = exportWeek;

